import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Send, Eye, Loader2, CheckCircle2, AlertCircle, Users } from "lucide-react";
import AdminTopNav from "../components/AdminTopNav";
import { toast } from "sonner";
import axios from "axios";
import { BACKEND_URL } from "../lib/apiBase";

const API = `${BACKEND_URL}/api`;

const CAMPAIGNS = {
  "transparency-statement": {
    title: "Transparenz-Statement 📄",
    desc: "Persönliches Statement vom 28.10.2026 (Trading-Pause & Systemüberarbeitung) von Adrian & Pascal — mit Statement im Wortlaut, PDF-Download und beruhigender Info-Box. Du wählst unten selbst aus, welche Gruppen die Mail erhalten.",
    audience: "Empfänger",
    supportsAudiences: true,
  },
  "autumn-sale": {
    title: "Herbst-Sale-Mail 🍂",
    desc: "Herbstaktion: Nur 20% Performance-Fee bis 31. Oktober. Geht an ALLE Kontakte (Kunden, Partner, Leads, Anfragen) — dedupliziert, ohne Demo-Accounts und ohne Abmeldungen.",
    audience: "Kontakte",
  },
  "september-momentum": {
    title: "September-Momentum 📈",
    desc: "Saisonalitäts-Mail: September historisch unruhigster Börsenmonat (Bitcoin Ø −4,02%, S&P 500 Ø −0,6%), EZB- & Fed-Termine, Vorteile des KI-Systems. Leads sehen die Kontaktdaten ihres Empfehlungsgebers + persönlichen Registrationslink (Fallback: Alphalane direkt / gubnr). Kunden bekommen die Info-Version (Dashboard-CTA, ohne Verkaufs-Pitch). Test-Mail enthält beide Versionen.",
    audience: "Leads & Kunden",
  },
  "juli-update": {
    title: "Juli-Update 📊",
    desc: "Transparentes Monats-Update: Juli leicht negativ (−2,24%), Einordnung mit Gesamtrendite, Fokus auf August + Hinweis auf den neuen Dashboard-Button. Geht an Kunden & Partner — jeweils mit angepasster Version (Test-Mail enthält beide Versionen).",
    audience: "Kunden & Partner",
  },
  "summer-sale": {
    title: "Sommer-Sale-Mail ☀️",
    desc: "Sommeraktion: Nur 30% Performance-Fee bis 31. August. Geht an ALLE Kontakte (Kunden, Partner, Leads, Anfragen) — dedupliziert, ohne Demo-Accounts und ohne Abmeldungen.",
    audience: "Kontakte",
  },
  "dashboard-launch": {
    title: "Dashboard-Launch-Mail",
    desc: "Einmalige Ankündigung an alle Kunden, dass das Live-Dashboard nun verfügbar ist. Wird nur an Kunden geschickt, die noch keine Launch-Mail erhalten haben.",
    audience: "Kunden",
  },
};

const AUDIENCE_OPTIONS = [
  { key: "customers", label: "Kunden", hint: "alle Kunden-Accounts (ohne Opt-outs)" },
  { key: "partners", label: "Partner", hint: "alle Partner-Accounts" },
  { key: "leads", label: "Leads", hint: "Stufe-2 Interessenten" },
  { key: "contacts", label: "Kontakte", hint: "Kontakt-Anfragen" },
];

export default function AdminBroadcast() {
  const navigate = useNavigate();
  const token = typeof window !== "undefined" ? localStorage.getItem("alphalane_admin_token") : null;
  const headers = useMemo(() => ({ "X-Admin-Token": token || "" }), [token]);

  const [campaign, setCampaign] = useState("transparency-statement");
  const meta = CAMPAIGNS[campaign];

  const [audiences, setAudiences] = useState(() => new Set(["customers"]));
  const audienceCsv = useMemo(
    () => AUDIENCE_OPTIONS.filter((o) => audiences.has(o.key)).map((o) => o.key).join(","),
    [audiences]
  );

  const toggleAudience = (key) => {
    setAudiences((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      if (next.size === 0) next.add("customers");
      return next;
    });
  };

  const audienceLabel = useMemo(() => {
    if (!meta.supportsAudiences) return meta.audience;
    return AUDIENCE_OPTIONS.filter((o) => audiences.has(o.key)).map((o) => o.label).join(" + ") || "Empfänger";
  }, [meta, audiences]);

  const [testEmail, setTestEmail] = useState("kontakt@moonshine-solutions.com");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null); // "preview" | "test" | "live" | null
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!token) navigate("/admin/login");
  }, [token, navigate]);

  const previewReqId = useMemo(() => ({ current: 0 }), []);

  const loadPreview = async () => {
    const reqId = ++previewReqId.current;
    setBusy("preview");
    try {
      const params = meta.supportsAudiences ? { audiences: audienceCsv } : undefined;
      const r = await axios.get(`${API}/admin/broadcast/${campaign}/preview`, { headers, params });
      if (reqId === previewReqId.current) setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      if (reqId === previewReqId.current) setBusy(null);
    }
  };

  useEffect(() => { setPreview(null); setResult(null); loadPreview(); }, [campaign, audienceCsv]); // eslint-disable-line

  const sendTest = async () => {
    if (!testEmail || !testEmail.includes("@")) {
      toast.error("Bitte gültige E-Mail-Adresse eingeben");
      return;
    }
    setBusy("test");
    setResult(null);
    try {
      const r = await axios.post(
        `${API}/admin/broadcast/${campaign}`,
        { mode: "test", test_recipient: testEmail, audiences: meta.supportsAudiences ? audienceCsv : undefined },
        { headers }
      );
      setResult({ kind: "test", data: r.data });
      if (r.data.ok) toast.success(`Test-Mail an ${testEmail} versandt`);
      else toast.error("Resend hat den Versand abgelehnt — siehe Backend-Logs");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBusy(null);
    }
  };

  const sendLive = async () => {
    const pending = preview?.pending ?? 0;
    if (!pending) {
      toast.error("Keine offenen Empfänger — alle wurden bereits angeschrieben.");
      return;
    }
    const msg = `Wirklich an ${pending} Empfänger (${audienceLabel}) verschicken?\n\n(Bereits versandte Empfänger werden übersprungen.)`;
    if (!window.confirm(msg)) return;
    setBusy("live");
    setResult(null);
    try {
      const r = await axios.post(
        `${API}/admin/broadcast/${campaign}`,
        { mode: "live", audiences: meta.supportsAudiences ? audienceCsv : undefined },
        { headers, timeout: 600000 }
      );
      setResult({ kind: "live", data: r.data });
      toast.success(`${r.data.sent_ok} von ${r.data.attempted} Mails erfolgreich versandt`);
      loadPreview();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--al-bg)]">
      <AdminTopNav />
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8">

        <div className="mb-8">
          <div className="text-xs uppercase tracking-widest font-semibold text-violet-600">Broadcast</div>
          <div className="flex flex-wrap items-center gap-2 mt-2 mb-3" data-testid="broadcast-campaign-switch">
            {Object.entries(CAMPAIGNS).map(([slug, c]) => (
              <button
                key={slug}
                type="button"
                onClick={() => setCampaign(slug)}
                data-testid={`broadcast-campaign-${slug}`}
                className={`px-4 py-1.5 rounded-full text-sm font-display font-semibold border transition-colors ${
                  campaign === slug
                    ? "bg-violet-600 text-white border-violet-600"
                    : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                }`}
              >
                {c.title}
              </button>
            ))}
          </div>
          <h1 className="font-display font-bold text-3xl mt-1">{meta.title}</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            {meta.desc} Zwischen den Sends liegt eine kleine Pause (Resend-Rate-Limit-Schutz).
          </p>
        </div>

        {/* Audience selection (nur bei Kampagnen mit Gruppen-Auswahl) */}
        {meta.supportsAudiences && (
          <div className="al-card p-5 sm:p-7 mb-6" data-testid="broadcast-audiences">
            <div className="flex items-center gap-2 mb-1">
              <Users className="h-4 w-4 text-violet-600" />
              <div className="font-display font-bold text-base">Empfängergruppen wählen</div>
            </div>
            <p className="text-xs text-slate-600 mb-4">
              Wähle aus, wer die Mail erhalten soll. Doppelte E-Mail-Adressen über mehrere Gruppen hinweg werden automatisch dedupliziert — niemand bekommt die Mail zweimal.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {AUDIENCE_OPTIONS.map(({ key, label, hint }) => {
                const active = audiences.has(key);
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => toggleAudience(key)}
                    data-testid={`broadcast-audience-${key}`}
                    className={`text-left rounded-xl border px-3 py-2.5 transition-all ${
                      active
                        ? "border-violet-500 bg-violet-50 ring-2 ring-violet-200"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex h-4 w-4 items-center justify-center rounded border-2 ${
                        active ? "border-violet-600 bg-violet-600" : "border-slate-300 bg-white"
                      }`}>
                        {active && <span className="text-white text-[10px] leading-none">✓</span>}
                      </span>
                      <span className="font-display font-semibold text-sm text-slate-900">{label}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 ml-6 leading-tight">{hint}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Recipient stats */}
        <div className="al-card p-5 sm:p-7 mb-6" data-testid="broadcast-preview">
          <div className="flex items-baseline justify-between gap-3 flex-wrap mb-5">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-slate-500" />
              <div className="font-display font-bold text-base">Empfänger-Übersicht</div>
            </div>
            <button
              type="button"
              onClick={loadPreview}
              disabled={busy === "preview"}
              className="text-xs font-semibold text-violet-700 hover:text-violet-900 disabled:opacity-50 inline-flex items-center gap-1"
              data-testid="broadcast-refresh"
            >
              {busy === "preview" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />} Aktualisieren
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard label={`Gesamt ${audienceLabel}`} value={preview?.total ?? "…"} color="slate" testid="broadcast-stat-total" />
            <StatCard label="Noch offen" value={preview?.pending ?? "…"} color="violet" testid="broadcast-stat-pending" />
            <StatCard label="Bereits versandt" value={preview?.already_sent ?? "…"} color="emerald" testid="broadcast-stat-sent" />
          </div>

          {preview?.recipients?.length > 0 && (
            <div className="mt-5 max-h-72 overflow-y-auto rounded-xl border border-[var(--al-line)] bg-white">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-slate-500 bg-slate-50 sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-2.5">E-Mail</th>
                    <th className="text-left px-4 py-2.5">Name</th>
                    <th className="text-left px-4 py-2.5">Status</th>
                    {meta.supportsAudiences && <th className="text-left px-4 py-2.5">Gruppe</th>}
                    <th className="text-right px-4 py-2.5">Mail</th>
                  </tr>
                </thead>
                <tbody data-testid="broadcast-recipients-body">
                  {preview.recipients.map((r) => (
                    <tr key={r.email} className="border-t border-[var(--al-line)]">
                      <td className="px-4 py-2 font-mono text-xs">{r.email}</td>
                      <td className="px-4 py-2 text-xs">{r.first_name} {r.last_name}</td>
                      <td className="px-4 py-2 text-xs text-slate-600">{r.status || "—"}</td>
                      {meta.supportsAudiences && (
                        <td className="px-4 py-2 text-xs text-slate-600">
                          {r.cohort ? (AUDIENCE_OPTIONS.find((o) => o.key === r.cohort)?.label || r.cohort) : "—"}
                        </td>
                      )}
                      <td className="px-4 py-2 text-xs text-right">
                        {r.already_sent
                          ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-3 w-3" /> Versandt</span>
                          : <span className="text-violet-600 font-semibold">Offen</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Test send */}
        <div className="al-card p-5 sm:p-7 mb-6" data-testid="broadcast-test">
          <div className="flex items-start gap-4">
            <div className="h-11 w-11 rounded-2xl bg-indigo-100 text-indigo-600 flex items-center justify-center flex-shrink-0">
              <Mail className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-display font-bold text-base">1) Test-Mail versenden</h2>
              <p className="text-xs text-slate-600 mt-1 mb-3">
                Verschicke die Mail zuerst an dich selbst, um Layout und Inhalt zu prüfen.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="kontakt@example.com"
                  data-testid="broadcast-test-email"
                  className="al-input-sm !py-2 !px-3 text-sm flex-1 min-w-[240px]"
                />
                <button
                  onClick={sendTest}
                  disabled={busy !== null}
                  data-testid="broadcast-test-send"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-display font-semibold disabled:opacity-50 transition-all"
                >
                  {busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Test versenden
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Live send */}
        <div className="al-card p-5 sm:p-7 mb-6 border-2 border-violet-200 bg-gradient-to-br from-violet-50 via-white to-fuchsia-50" data-testid="broadcast-live">
          <div className="flex items-start gap-4">
            <div className="h-11 w-11 rounded-2xl bg-violet-600 text-white flex items-center justify-center flex-shrink-0">
              <Send className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-display font-bold text-base">2) Live-Broadcast an: {audienceLabel}</h2>
              <p className="text-xs text-slate-600 mt-1 mb-3">
                Sendet die Mail an alle angewählten Empfänger ({audienceLabel}), die sie noch nicht erhalten haben.
                <strong className="text-slate-900"> Nicht rückgängig zu machen.</strong> Empfänger, die
                bereits eine Mail erhalten haben, werden automatisch übersprungen.
              </p>
              <button
                onClick={sendLive}
                disabled={busy !== null || !preview?.pending}
                data-testid="broadcast-live-send"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full al-grad-bg text-white text-sm font-display font-semibold disabled:opacity-50 transition-all shadow-md"
              >
                {busy === "live" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                An {preview?.pending ?? 0} Empfänger ({audienceLabel}) versenden
              </button>
            </div>
          </div>
        </div>

        {/* Result */}
        {result && (
          <div className="al-card p-5 sm:p-7" data-testid="broadcast-result">
            <div className="flex items-center gap-2 mb-3">
              {result.data.ok !== false && (result.data.failed_count ?? 0) === 0
                ? <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                : <AlertCircle className="h-5 w-5 text-amber-600" />}
              <h3 className="font-display font-bold text-base">
                {result.kind === "test" ? "Test-Versand" : "Live-Broadcast"} — Ergebnis
              </h3>
            </div>
            <pre className="text-xs bg-slate-50 rounded-lg p-4 overflow-x-auto max-h-72" data-testid="broadcast-result-json">
              {JSON.stringify(result.data, null, 2)}
            </pre>
          </div>
        )}

      </div>
    </div>
  );
}

function StatCard({ label, value, color, testid }) {
  const map = {
    slate: "bg-slate-50 text-slate-900 border-slate-200",
    violet: "bg-violet-50 text-violet-900 border-violet-200",
    emerald: "bg-emerald-50 text-emerald-900 border-emerald-200",
  };
  return (
    <div className={`rounded-xl border p-4 ${map[color] || map.slate}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-semibold opacity-70">{label}</div>
      <div className="font-display font-bold text-3xl mt-1">{value}</div>
    </div>
  );
}
